import { RadioButton } from '@components/RadioButton';
import { markdownComponents } from '@components/contentBlock/markdownBlock';
import { Select, reactSelectStyles } from '@components/selectbox/lib/SelectboxStyles';
import { useCallback, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import type { ActionMeta, SingleValue } from 'react-select';
import { VisitorDropDownIndicator } from './VisitorDropDownIndicator';
import type { ContentProps } from './types';

export const Content: React.FC<ContentProps> = ({ page, onVisitorTypeChange, visitorInfo, setVisitorInfo, onConfirm, onReject }) => {
  const hasVisitorTypeOptions = useMemo(() => page.visitorType.options.length > 0, [page.visitorType.options.length]);
  const hasCountryOptions = useMemo(() => page.country.options.length > 0, [page.country.options.length]);

  const { convertedOptions } = useMemo(() => {
    const itemsByKey: Record<string, any> = {};
    const options = page.country.options.map((option) => {
      itemsByKey[option.code || ''] = option;
      return {
        value: option.code || '',
        label: option.label,
      };
    });
    return {
      convertedOptions: options,
      itemsByKey,
    };
  }, [page.country.options]);

  const onCountryChange = useCallback(
    (v: SingleValue<{ value: string; label: string | null }>, _actionMeta: ActionMeta<{ value: string; label: string | null }>) => {
      setVisitorInfo({ ...visitorInfo, country: v?.value ?? null });
    },
    [setVisitorInfo, visitorInfo],
  );

  // The country dropdown is only rendered when the backend supplies both options
  // and a section title. Some disclaimers (e.g. publications) omit the country
  // field entirely, so it must never be rendered or required there.
  const isCountrySectionShown = hasCountryOptions && typeof page.country.title === 'string' && page.country.title !== '';

  // Only require a country selection when the dropdown is actually shown AND
  // interactive (more than one option; it is rendered disabled for a single
  // option). Otherwise the user could be blocked with no way to choose. An empty
  // string is the "Other countries" option (null code): it counts as chosen so
  // the button enables, but confirming it rejects the visitor.
  const requiresCountrySelection = isCountrySectionShown && page.country.options.length > 1;
  const isCountryChosen = !requiresCountrySelection || typeof visitorInfo.country === 'string';

  const onConfirmClick = useCallback(() => {
    // "Other countries" (null code, mapped to an empty value) is not served, so
    // confirming that selection rejects the visitor instead of proceeding.
    if (visitorInfo.country === '') {
      onReject();
      return;
    }
    onConfirm();
  }, [visitorInfo.country, onReject, onConfirm]);

  return (
    <div>
      <h1>{page.pageTitle}</h1>
      {page.explanation ? (
        <div className="Base-container">
          <div>{page.explanation}</div>
        </div>
      ) : null}

      {hasVisitorTypeOptions && page.visitorType.title && page.visitorType.title !== '' ? (
        <div className="flex flex-row flex-wrap border-none transition-[0.5s] justify-start items-baseline w-full my-10">
          <div className="text-black w-62.5 text-xl">{page.visitorType.title}</div>
          <div className="grow [&>label]:w-full [&>label]:text-black [&>label]:grow [&>label]:text-base">
            {page.visitorType.options.map(
              (option) => option.label && <RadioButton key={option.code} name="visitorType" label={option.label} value={option.code} checked={option.code === visitorInfo.code} onChange={onVisitorTypeChange} />,
            )}
          </div>
        </div>
      ) : null}
      {isCountrySectionShown ? (
        <div className="flex flex-row flex-wrap border-none transition-[0.5s] justify-start items-baseline w-full my-10">
          <div className="text-black w-62.5 text-xl">{page.country.title}</div>
          <div className="grow [&>label]:w-full [&>label]:text-black [&>label]:grow [&>label]:text-base">
            <div className="VisitorInfo-react-select">
              <Select
                value={convertedOptions.find((opt) => opt.value === visitorInfo.country) || null}
                options={convertedOptions}
                onChange={onCountryChange}
                isSearchable={false}
                isClearable={false}
                isDisabled={page.country.options.length <= 1}
                classNamePrefix="rs-visitor"
                styles={reactSelectStyles}
                components={{
                  DropdownIndicator: VisitorDropDownIndicator,
                  IndicatorSeparator: null,
                }}
                menuPortalTarget={document.body}
              />
            </div>
          </div>
        </div>
      ) : null}

      <div className={'my-7.5 mb-12.5 Base-disclaimer disclaimer'}>
        <div className="my-8.75 leading-5.25 first:mt-0 self-stretch">
          <ReactMarkdown components={markdownComponents}>{page.disclaimer}</ReactMarkdown>
        </div>
      </div>

      <div className="flex flex-row flex-wrap gap-2 border-none justify-end items-baseline w-full my-2.5">
        <button
          type="button"
          className="ml-10 inline-block cursor-pointer rounded-[30px] border border-black bg-white px-10 py-2.5 text-[15px] font-normal uppercase tracking-wide text-accent outline-none my-2.5"
          onClick={(e) => {
            e.preventDefault();
            onReject();
          }}
        >
          {page.rejectLabel}
        </button>
        <button
          type="button"
          disabled={!isCountryChosen}
          className="inline-block cursor-pointer rounded-[30px] border border-black bg-white px-10 py-2.5 text-[15px] font-normal uppercase tracking-wide text-accent outline-none my-2.5 disabled:cursor-not-allowed disabled:opacity-40"
          onClick={(e) => {
            e.preventDefault();
            onConfirmClick();
          }}
        >
          {page.confirmLabel}
        </button>
      </div>
    </div>
  );
};
